import { yupResolver } from "@hookform/resolvers/yup";
import { Empty } from "antd";
import FormInput from "components/Common/Form/FormInput";
import ConfirmModal from "components/shared-components/ConfirmModal";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import {
  useListCollectorIsFree,
  useListHubAdminNotManageHub,
  useListLogAdminNotManageCenter,
  useListRunnerIsFree,
} from "hooks";
import {
  useCreateHubUser,
  useDeleteHubUser,
  useUpdateHubUser,
} from "hooks/hub-user/useHubUser";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Label,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import { optionsRole } from "../utils";
import { validateSchema } from "./validate";
import { useDetailHub } from "hooks/hub-management/useHub";

const HubUserRow = ({
  dataRow,
  optionsRole,
  listOptions,
  viewMode,
  isCenter,
  refetchHubUser,
}) => {
  const { t } = useTranslation(["hub"]);
  const [isEdit, setIsEdit] = useState(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);

  const mainForm = useForm({
    resolver: yupResolver(validateSchema(t)),
    mode: "all",
    reValidateMode: "onChange",
  });
  const {
    reset,
    control,
    setValue,
    getValues,
    formState: { errors },
    watch,
  } = mainForm;
  const userAccount = watch("user_account");
  const userRole = watch("user_role");

  const onSuccessUpdateUser = () => {
    refetchHubUser();
    setIsEdit(false);
  };

  const onSuccessDeleteUser = () => {
    refetchHubUser();
    setIsOpenConfirmModal(false);
  };

  const { mutate: updateHubUser } = useUpdateHubUser(onSuccessUpdateUser);
  const { mutate: deleteHubUser } = useDeleteHubUser(onSuccessDeleteUser);

  useEffect(() => {
    setDefaultData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCenter, refetchHubUser, setValue, dataRow]);

  useEffect(() => {
    if (userAccount) {
      let account = {};
      if (userRole?.value === 1) {
        account = listOptions?.logAdmin?.find(
          item => item.value === userAccount.value
        );
      } else if (userRole?.value === 2) {
        account = listOptions?.runner?.find(
          item => item.value === userAccount.value
        );
      } else if (userRole?.value === 3) {
        account = listOptions?.hubAdmin?.find(
          item => item.value === userAccount.value
        );
      } else if (userRole?.value === 4) {
        account = listOptions?.collector?.find(
          item => item.value === userAccount.value
        );
      }

      setValue("user_name", account?.display_name);
    }
  }, [userAccount, userRole, listOptions, setValue]);

  const setDefaultData = () => {
    const main = {
      label: dataRow.user?.username,
      value: dataRow.user?.id,
    };
    reset({
      user_account: main,
      user_name: dataRow.user?.display_name,
      user_role: optionsRole[dataRow.type - 1],
    });
  };

  const onCancel = () => {
    setDefaultData();
    setIsEdit(false);
  };

  const onSave = () => {
    if (Object.keys(errors).length === 0) {
      const user_account = getValues("user_account");
      const user_role = getValues("user_role");

      updateHubUser({
        hubUserId: dataRow.id,
        data: {
          user_id: user_account.value,
          type: user_role.value,
          hub_id: dataRow.hub_id,
        },
      });
    }
  };

  const onDelete = () => {
    deleteHubUser(dataRow?.id);
  };

  const userAccountOptions = useMemo(() => {
    if (userRole?.value !== dataRow.type) {
      setValue("user_account", null);
    }

    if (userRole?.value === 1) {
      return listOptions?.logAdmin;
    } else if (userRole?.value === 2) {
      return listOptions?.runner;
    } else if (userRole?.value === 3) {
      return listOptions?.hubAdmin;
    } else if (userRole?.value === 4) {
      return listOptions?.collector;
    } else {
      return [];
    }
  }, [userRole, listOptions, dataRow, setValue]);

  return (
    <Row>
      <Col xs={viewMode ? 4 : 3}>
        <FormGroup>
          <FormInput
            isLabel={false}
            t={t}
            fieldName={`user_name`}
            type="text"
            mainForm={mainForm}
            title={t("form.user_name")}
            isDisabled
          />
        </FormGroup>
      </Col>
      <Col xs={viewMode ? 4 : 3}>
        <FormGroup>
          <Controller
            control={control}
            name="user_role"
            render={({ field }) => (
              <Select
                {...field}
                placeholder={t("placeholder.select", {
                  title: t("form.user_role"),
                })}
                options={optionsRole.filter(item =>
                  isCenter ? item.value !== 3 : item.value !== 1
                )}
                isSearchable
                className="basic-single w-100"
                classNamePrefix="select"
                isDisabled={viewMode ? true : !isEdit}
              />
            )}
          />
        </FormGroup>
      </Col>
      <Col xs={4}>
        <FormGroup>
          <Controller
            control={control}
            name="user_account"
            render={({ field }) => (
              <Select
                {...field}
                placeholder={t("placeholder.select", {
                  title: t("form.user_account"),
                })}
                options={userAccountOptions}
                isSearchable
                className="basic-single w-100"
                classNamePrefix="select"
                isDisabled={viewMode ? true : !isEdit}
              />
            )}
          />
          {errors.user_account && (
            <MessageFormValidate error={errors.user_account.message} />
          )}
        </FormGroup>
      </Col>
      {!viewMode && (
        <Col xs={2}>
          {!isEdit && (
            <>
              <UncontrolledTooltip placement="top" target="btnEdit">
                {t("button.update")}
              </UncontrolledTooltip>
              <button
                id="btnEdit"
                type="button"
                className="btn"
                onClick={() => setIsEdit(true)}
              >
                <i className="fas fa-pen fa-2xs text-info" />
              </button>
              <UncontrolledTooltip placement="top" target="btnDelete">
                {t("button.delete")}
              </UncontrolledTooltip>
              <button
                onClick={() => setIsOpenConfirmModal(true)}
                id="btnDelete"
                type="button"
                className="btn"
              >
                <i className="fas fa-trash fa-2xs text-danger" />
              </button>
            </>
          )}
          {isEdit && (
            <>
              <Button
                size="sm"
                color="primary"
                id="btnDelete"
                type="button"
                className="btn btn-primary"
                onClick={onSave}
              >
                {t("button.save")}
              </Button>
              <Button
                size="sm"
                color="danger"
                id="btnCanel"
                type="button"
                className="btn btn-primary mx-2 my-1"
                onClick={onCancel}
              >
                {t("button.cancel")}
              </Button>
            </>
          )}
        </Col>
      )}
      <ConfirmModal
        isOpen={isOpenConfirmModal}
        titleHeader={t("actions.confirmActiveModal.titleDeleteUser")}
        titleBody={t("actions.confirmActiveModal.contentDeleteUser")}
        onConfirm={onDelete}
        textBtnConfirm={t("actions.confirmActiveModal.confirmBtn")}
        textBtnCancel={t("actions.confirmActiveModal.cancelBtn")}
        toggle={() => setIsOpenConfirmModal(false)}
      />
    </Row>
  );
};

const AddHubUser = ({
  optionsRole,
  listOptions,
  viewMode,
  isCenter,
  refetchHubUser,
  setIsAddNew,
  hubId,
}) => {
  const { t } = useTranslation(["hub"]);

  const mainForm = useForm({
    resolver: yupResolver(validateSchema(t)),
    mode: "all",
    reValidateMode: "onChange",
  });
  const {
    reset,
    control,
    setValue,
    getValues,
    formState: { errors },
    watch,
  } = mainForm;
  const userAccount = watch("user_account");
  const userRole = watch("user_role");

  const onSuccessCreateUser = () => {
    refetchHubUser();
    cancelAdd();
  };
  const { mutate: createHubUser } = useCreateHubUser(onSuccessCreateUser);

  const cancelAdd = () => {
    setIsAddNew(false);
    reset({});
  };

  const onSave = () => {
    if (Object.keys(errors).length === 0) {
      const user_account = getValues("user_account");
      const user_role = getValues("user_role");

      createHubUser({
        hub_id: hubId,
        user_id: user_account.value,
        type: user_role.value,
      });
    }
  };

  const userAccountOptions = useMemo(() => {
    setValue("user_account", null);
    if (userRole?.value === 1) {
      return listOptions?.logAdmin;
    } else if (userRole?.value === 2) {
      return listOptions?.runner;
    } else if (userRole?.value === 3) {
      return listOptions?.hubAdmin;
    } else if (userRole?.value === 4) {
      return listOptions?.collector;
    } else {
      return [];
    }
  }, [userRole, listOptions, setValue]);

  useEffect(() => {
    if (userAccount) {
      const account = userAccountOptions?.find(
        item => item.value === userAccount.value
      );

      setValue("user_name", account?.display_name);
    } else {
      setValue("user_name", "");
    }
  }, [userAccountOptions, userAccount, setValue]);

  return (
    <>
      <Row>
        <Col xs={viewMode ? 4 : 3}>
          <FormGroup>
            <FormInput
              isLabel={false}
              t={t}
              fieldName={`user_name`}
              type="text"
              mainForm={mainForm}
              title={t("form.user_name")}
              isDisabled
            />
          </FormGroup>
        </Col>
        <Col xs={viewMode ? 4 : 3}>
          <FormGroup>
            <Controller
              control={control}
              name="user_role"
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder={t("placeholder.select", {
                    title: t("form.user_role"),
                  })}
                  options={optionsRole.filter(item =>
                    isCenter ? item.value !== 3 : item.value !== 1
                  )}
                  isSearchable
                  className="basic-single w-100"
                  classNamePrefix="select"
                />
              )}
            />
          </FormGroup>
        </Col>
        <Col xs={4}>
          <FormGroup>
            <Controller
              control={control}
              name="user_account"
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder={t("placeholder.select", {
                    title: t("form.user_account"),
                  })}
                  options={userAccountOptions}
                  isSearchable
                  className="basic-single w-100"
                  classNamePrefix="select"
                  isDisabled={viewMode}
                />
              )}
            />
            {errors.user_account && (
              <MessageFormValidate error={errors.user_account.message} />
            )}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col className="text-start">
          <Button
            size="xs"
            style={{ backgroundColor: "#3C64B1" }}
            onClick={onSave}
            className="btn btn-primary btn-label"
          >
            <i className="fas fa-save label-icon"></i>
            {t("button.save")}
          </Button>
          <Button size="xs" onClick={cancelAdd} className="btn btn-danger mx-2">
            {t("button.cancel")}
          </Button>
        </Col>
      </Row>
    </>
  );
};

const HubUserFormSection = ({
  hubId,
  isCenter,
  listHubUser,
  refetchHubUser,
  viewMode,
}) => {
  const { t } = useTranslation(["hub"]);
  const [isAddNew, setIsAddNew] = useState(false);

  const { data: hubDetail, refetch: refetchHubDetail } = useDetailHub({
    hubId,
    params: {},
  });

  const isNewAddress = hubDetail?.is_new_address;

  const { data: listLogAdmin, refetch: refetchLogAdmin } =
    useListLogAdminNotManageCenter({
      params: { is_new_address: isNewAddress },
    });
  const { data: listHubAdmin, refetch: refetchHubAdmin } =
    useListHubAdminNotManageHub({
      params: { is_new_address: isNewAddress },
    });
  const { data: listRunner, refetch: refetchRunner } = useListRunnerIsFree({
    params: { is_new_address: isNewAddress },
  });
  const { data: listCollector, refetch: refetchCollector } =
    useListCollectorIsFree({
      params: { is_new_address: isNewAddress },
    });

  useEffect(() => {
    if (hubId) {
      refetchHubDetail();
    }
  }, [hubId, refetchHubDetail]);

  useEffect(() => {
    if (!isNewAddress) return;
    if (isCenter) {
      refetchLogAdmin();
    } else {
      refetchHubAdmin();
    }
    refetchRunner();
    refetchCollector();
  }, [
    isCenter,
    isNewAddress,
    refetchLogAdmin,
    refetchHubAdmin,
    refetchRunner,
    refetchCollector,
  ]);
  const hubUser = listHubUser?.items;

  const listOptions = useMemo(() => {
    const runner =
      listRunner?.data?.items?.map(item => ({
        label: item.username,
        value: item.id,
        display_name: item.display_name,
      })) || [];
    const collector =
      listCollector?.data?.items?.map(item => ({
        label: item.username,
        value: item.id,
        display_name: item.display_name,
      })) || [];

    if (isCenter) {
      return {
        runner,
        collector,
        logAdmin:
          listLogAdmin?.data?.items?.map(item => ({
            label: item.username,
            value: item.id,
            display_name: item.display_name,
          })) || [],
      };
    } else {
      return {
        runner,
        collector,
        hubAdmin:
          listHubAdmin?.data?.items?.map(item => ({
            label: item.username,
            value: item.id,
            display_name: item.display_name,
          })) || [],
      };
    }
  }, [listLogAdmin, listHubAdmin, isCenter, listRunner, listCollector]);

  // useEffect(() => {
  //   if (isCenter) {
  //     refetchLogAdmin();
  //   } else {
  //     refetchHubAdmin();
  //   }
  //   refetchRunner();
  //   refetchCollector();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isCenter, refetchLogAdmin, refetchHubAdmin, refetchRunner]);

  return (
    <Card style={{ marginBottom: 0 }}>
      <CardBody style={{ minWidth: "700px" }}>
        {viewMode && (!hubUser || hubUser.length < 1) && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
        {hubUser && hubUser.length > 0 && (
          <>
            <Row>
              <Col xs={viewMode ? 4 : 3}>
                <FormGroup>
                  <Label>{t("form.user_name")}</Label>
                </FormGroup>
              </Col>
              <Col xs={viewMode ? 4 : 3}>
                <FormGroup>
                  <Label>{t("form.user_role")}</Label>
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label>
                    {t("form.user_account")}{" "}
                    <span className="text-danger">*</span>
                  </Label>
                </FormGroup>
              </Col>
              <Col xs={2}></Col>
            </Row>
            {hubUser.map(row => (
              <HubUserRow
                key={row?.id}
                dataRow={row}
                isCenter={isCenter}
                optionsRole={optionsRole}
                listOptions={listOptions}
                viewMode={viewMode}
                refetchHubUser={refetchHubUser}
              />
            ))}
          </>
        )}
        {isAddNew && (
          <AddHubUser
            isCenter={isCenter}
            optionsRole={optionsRole}
            listOptions={listOptions}
            viewMode={viewMode}
            refetchHubUser={refetchHubUser}
            setIsAddNew={setIsAddNew}
            hubId={hubId}
          />
        )}
        {!viewMode && !isAddNew && (
          <Row>
            <Col className="text-start">
              <Button
                size="xs"
                style={{ backgroundColor: "#3C64B1" }}
                onClick={() => setIsAddNew(true)}
                className="btn btn-primary btn-label"
              >
                <i className="fas fa-plus label-icon"></i>
                {t("button.add", { target: t("title.user") })}
              </Button>
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  );
};

export default HubUserFormSection;
